import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useSession} from "../../../auth/SessionContext";
import RequestMetadataBox from "../../RequestMetadataBox";
import ZeeroButton from "../../custom/ZeeroButton";
import {faFilePdf, faVideo, faXmark} from "@awesome.me/kit-92187fb46c/icons/classic/regular";
import {faCircleQuestion, faPlus} from "@awesome.me/kit-92187fb46c/icons/classic/solid";
import {useDropzone} from "react-dropzone";
import {
	ALLOWED_FILE_TYPES,
	AllowedMimeTypes,
	DicomizeProgress,
	DicomizeStatus,
	FileType,
	getFileType
} from "../../../types/Dicomize";
import {buildDicomizeFormData} from "./DicomizeHandler";
import * as http from "../../../handlers/http"
import {ProgressPoller} from "../../../handlers/ProgressHandler";
import ProgressPopup from "../../ProgressPopup";
import {DicomizeData} from "../../../types/Session";
import {ErrorCode, FormDataAbortedError} from "../../../types/Error";
import {toast} from "../../custom/toasters/ToastersManager";
import {Trans, useTranslation} from "react-i18next";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

const MAX_FILES = 100; // the same value is set in the server project

const sameFile = (a: File, b: File) => a.name === b.name && a.lastModified === b.lastModified;

const isValidFileType = (file: File) =>
		["image/png", "image/jpeg", "video/mp4", "application/pdf"].includes(file.type);

export default function DicomizePage(): ReactElement {
	const {t} = useTranslation();
	const [filesNumber, setDroppedFilesNumber] = useState<number>(0);
	const [validFiles, setValidFiles] = useState<File[]>([]);
	const [dropped, setDropped] = useState<boolean>(false);
	const [showProgressPopup, setShowProgressPopup] = useState<boolean>(false);

	const [dicomizeUuid, setDicomizeUuid] = useState<string | null>(null);
	const [dicomizeError, setDicomizeError] = useState<ErrorCode | null>(null);
	const [dicomizeStatus, setDicomizeStatus] = useState<DicomizeStatus>("IDLE");
	const [dicomizeCounts, setDicomizeCounts] = useState<{ handled: number; total: number }>({total: 0, handled: 0});

	const aborterRef = useRef<{ abort: () => void } | null>(null);
	const pollerRef = useRef<ProgressPoller<DicomizeProgress> | null>(null);

	const {session} = useSession();
	const requestMetadata = (session?.requestData as DicomizeData | undefined)?.metadata ?? null;

	const handleAcceptedFiles = useCallback((droppedFiles: File[]) => {
		const valid: File[] = [];
		for (let acceptedFile of droppedFiles) {
			if (isValidFileType(acceptedFile)) {
				valid.push(acceptedFile);
			}
		}

		if (valid.length > MAX_FILES) {
			toast.error(t("dicomize_maxFilesAmountExceeded"));
			return
		} else if (valid.length == 0) {
			toast.error(t("dicomize_noValidFile"));
			return;
		}

		setDroppedFilesNumber(droppedFiles.length);
		setValidFiles(valid)
		setDropped(true);
	}, []);

	const onAddNewFiles = useCallback((droppedFiles: File[]) => {
		let newValidFiles = droppedFiles.filter(
				(file) =>
						isValidFileType(file) &&
						!validFiles.some((existing) => sameFile(existing, file))
		);

		if (newValidFiles.length === 0) {
			toast.error(t("dicomize_noValidFile"));
			return;
		}

		const totalAfterDrop = filesNumber + newValidFiles.length;
		if (totalAfterDrop > MAX_FILES) {
			const remainingSlots = MAX_FILES - filesNumber;

			if (remainingSlots <= 0) {
				toast.error(t("dicomize_maxFilesAmountExceeded"));
				return;
			}

			toast.warn(t("dicomize_maxFilesAmountExceeded"));
			newValidFiles = newValidFiles.slice(0, remainingSlots);
		}

		setDroppedFilesNumber(prev => prev + droppedFiles.length);
		setValidFiles([...validFiles, ...newValidFiles]);
		setDropped(true);
	}, [filesNumber, validFiles]);

	const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
		noClick: true,
		noKeyboard: true,
		onDrop: handleAcceptedFiles,
		useFsAccessApi: false,
	});

	const resetDicomize = useCallback(() => {
		setShowProgressPopup(false);

		setDicomizeUuid(null);
		setDicomizeError(null);
		setDicomizeStatus("IDLE");
		setDicomizeCounts({total: 0, handled: 0})
		aborterRef.current = null;
		pollerRef.current?.stop();
		pollerRef.current = null;
	}, []);

	const resetAll = useCallback(() => {
		setDropped(false);
		setDroppedFilesNumber(0)
		setValidFiles([]);

		resetDicomize();
	}, [resetDicomize]);

	const handleFileDelete = useCallback((fileToDelete: File) => {
		setValidFiles(prev => {
			const updated = prev.filter(file => !sameFile(file, fileToDelete))
			if (updated.length == 0) {
				setDropped(false);
			}

			return updated;
		});
	}, [validFiles]);

	const beginPolling = useCallback(async (uuid: string) => {
		const poller = new ProgressPoller<DicomizeProgress>();
		pollerRef.current = poller;

		await poller.start(uuid, {
			onProgress: (p) => {
				setDicomizeStatus(p.status);
				if (p.status == "DICOMIZING")
					setDicomizeCounts({handled: p.dicomized, total: p.total});
				else
					setDicomizeCounts({handled: p.sent, total: p.total});
			},
			onSuccess: (p) => {
				setDicomizeStatus("SUCCESS");
				setDicomizeCounts({handled: p.sent, total: p.total});
			},
			onError: (code) => {
				setDicomizeStatus("FAILED");
				setDicomizeError(code);
			},
		});
	}, []);

	const upload = useCallback(async () => {
		if (!validFiles || validFiles.length === 0) {
			return;
		}

		setShowProgressPopup(true);
		setDicomizeStatus("SENDING");
		const formData = buildDicomizeFormData(validFiles);
		try {
			const {promise, abort} = http.formData<{
				uuid: string
			}>("/web/dicomize", formData, (loaded, total) => {
				setDicomizeCounts({handled: loaded, total});
			});
			aborterRef.current = {abort};

			const data = await promise;
			setDicomizeUuid(data.uuid);
			await beginPolling(data.uuid);
		} catch (e) {
			if (e instanceof FormDataAbortedError) {
				console.error("user aborted upload")
				setDicomizeStatus("ABORTED");
			} else {
				setDicomizeStatus("FAILED");
				setDicomizeError(ErrorCode.SERVICE_FAILED);
			}
			const total = [...formData.entries()].length;
			setDicomizeCounts({handled: 0, total}); // nothing was sent
		} finally {
			aborterRef.current = null;
		}
	}, [validFiles, beginPolling]);

	const onAbort = useCallback(() => {
		setDicomizeStatus("ABORTING");

		aborterRef.current?.abort(); // this can happen before the upload session is initialized
		aborterRef.current = null;

		pollerRef.current?.stop();
		pollerRef.current = null;

		if (!dicomizeUuid) {
			return;
		}

		http.post<void>(`/web/abort?uuid=${encodeURIComponent(dicomizeUuid!)}`)
				.then(() => setDicomizeStatus("ABORTED"))
				.catch(() => {
					setDicomizeError(ErrorCode.SERVICE_ABORT_FAILED);
					setDicomizeStatus("FAILED")
				})
	}, [dicomizeUuid]);

	return (
			<div id="dicomize" className="page-content">
				<div className="page-title">{t("dicomize_dicomize")}</div>

				<div className="upload-grid">
					{dropped ? (
							<DroppedFilesRecap droppedFilesNumber={filesNumber} validFiles={validFiles}
											   onReset={resetAll} onDelete={handleFileDelete}
											   onAddNewFiles={onAddNewFiles} dicomizeStatus={dicomizeStatus}/>
					) : (
							<div {...getRootProps()}>
								<div className={`drop-zone-container ${isDragActive ? "active" : ""}`}>
									<input {...getInputProps({dir: ""})} webkitdirectory={""}/>
									<div id="drop-zone-text">
										{t("general_dropZoneText")} ({t("dicomize_maxFiles", {amount: MAX_FILES})})
									</div>
									<ZeeroButton size="xs" type="primary" onClick={open}>
										<FontAwesomeIcon icon={faPlus}/> {t("general_dropZoneButton")}
									</ZeeroButton>
								</div>
							</div>

					)}
					<RequestMetadataBox requestMetadata={requestMetadata} mismatches={new Map()}/>
				</div>

				{(dropped && validFiles.length > 0 && dicomizeStatus != "SUCCESS") && (
						<div className="upload-actions">
							<ZeeroButton
									type="primary"
									onClick={upload}
							>
								{t("general_upload")}
							</ZeeroButton>
						</div>
				)}

				{showProgressPopup && (
						<ProgressPopup
								status={dicomizeStatus}
								progress={dicomizeCounts}
								error={dicomizeError}
								onAbort={onAbort}
								onCancel={resetDicomize}
								closeAfterSuccess={false}
						/>
				)}
			</div>
	)
}

const DroppedFilesRecap = ({droppedFilesNumber, validFiles, onReset, onDelete, onAddNewFiles, dicomizeStatus}: {
	droppedFilesNumber: number,
	validFiles: File[],
	onReset: () => void,
	onDelete: (file: File) => void,
	onAddNewFiles: (files: File[]) => void,
	dicomizeStatus: DicomizeStatus
}): ReactElement => {
	const {t} = useTranslation();
	const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
		noClick: true,
		noKeyboard: true,
		onDrop: onAddNewFiles,
		useFsAccessApi: false,
	});

	const groupedFiles: Map<FileType, File[]> = useMemo(() => {
		const allowedFiles = validFiles.filter((f) => ALLOWED_FILE_TYPES.has(f.type as AllowedMimeTypes));

		const temp = new Map<FileType, File[]>();
		for (const file of allowedFiles) {
			const kind = getFileType(file.type as AllowedMimeTypes);
			if (!temp.has(kind)) {
				temp.set(kind, []);
			}
			temp.get(kind)!.push(file);
		}

		return temp;
	}, [validFiles]);

	return (
			<div id="valid-files" className="data-container">
				<div {...getRootProps()}>
					<div className={`drop-zone-container add-files-drop-zone ${isDragActive ? "active" : ""}`}>
						<input {...getInputProps({dir: ""})} webkitdirectory={""}/>
						<div id="drop-zone-text">
							{t("general_dropZoneText")} ({t("dicomize_maxFiles", {amount: MAX_FILES})})
						</div>
					</div>

					<div className="recap-header">
						<div>
							<div className="upload-data-title">{t("dicomize_parsedFiles")}</div>
							<div className="upload-data-subtitle">
								<Trans t={t}
									   i18nKey="general_parsedTotal"
									   count={droppedFilesNumber}
									   components={{strong: <strong/>}}
								/>{" "}
								{validFiles.length < droppedFilesNumber ? (
										<React.Fragment>
											<Trans t={t}
												   i18nKey="general_parsedDiscarded"
												   count={droppedFilesNumber - validFiles.length}
												   components={{strong: <strong/>}}
											/>{" "}
										</React.Fragment>
								) : null}
								{groupedFiles.has("IMAGE") &&
										<React.Fragment>
											<Trans t={t}
												   i18nKey="dicomize_imagesFound"
												   count={groupedFiles.get("IMAGE")?.length}
												   components={{strong: <strong/>}}
											/>{" "}
										</React.Fragment>
								}
								{groupedFiles.has("PDF") &&
										<React.Fragment>
											<Trans t={t}
												   i18nKey="dicomize_documentsFound"
												   count={groupedFiles.get("PDF")?.length}
												   components={{strong: <strong/>}}
											/>{" "}
										</React.Fragment>
								}
								{groupedFiles.has("VIDEO") &&
										<React.Fragment>
											<Trans t={t}
												   i18nKey="dicomize_videosFound"
												   count={groupedFiles.get("VIDEO")?.length}
												   components={{strong: <strong/>}}
											/>{" "}
										</React.Fragment>
								}
							</div>
						</div>
						<div className="recap-header-actions">
							{dicomizeStatus != "SUCCESS" && (
									<React.Fragment>
										<ZeeroButton type="primary" onClick={open}>
											<FontAwesomeIcon icon={faPlus}/> {t("general_dropZoneButton")}
										</ZeeroButton>
										<ZeeroButton type="primary" onClick={onReset}>
											{t("general_reset")}
										</ZeeroButton>
									</React.Fragment>
							)}
						</div>
					</div>

					<Previews groupedFiles={groupedFiles} onDelete={onDelete}/>
				</div>
			</div>
	)
}

const Previews = ({groupedFiles, onDelete}: {
	groupedFiles: Map<FileType, File[]>,
	onDelete: (file: File) => void
}): ReactElement => {
	return (
			<div id="files-preview" className="files-grid">
				{Array.from(groupedFiles.entries())
						.sort()
						.map(([kind, filesOfKind]) => (
								<React.Fragment key={kind}>
									<div className="file-type-break"/>
									{filesOfKind.map((file) => {
										const key = `${file.name}-${file.lastModified}`;
										return <Preview key={key} kind={kind} file={file} onDelete={onDelete}/>
									})}
								</React.Fragment>
						))}
			</div>
	);
};

const Preview = ({kind, file, onDelete}: { kind: FileType, file: File, onDelete: (file: File) => void }) => {
	const [imageUrl, setImageUrl] = useState<string | null>(null);

	useEffect(() => {
		if (kind == "IMAGE") {
			const url = URL.createObjectURL(file);
			setImageUrl(url);

			return () => {
				URL.revokeObjectURL(url);
			};
		}
	}, [file]);

	const previewBody = useMemo(() => {
		if (kind == "IMAGE" && imageUrl) {
			return <img title={file.name} src={imageUrl} loading="lazy" alt={file.name}/>;
		} else if (kind == "VIDEO") {
			return <FontAwesomeIcon icon={faVideo} size="4x"/>;
		} else if (kind == "PDF") {
			return <FontAwesomeIcon icon={faFilePdf} size="4x"/>;
		}

		return <FontAwesomeIcon icon={faCircleQuestion} size="4x"/>
	}, [kind, imageUrl, file]);

	return (
			<div className="preview-box image">
				{previewBody}
				<div title={file.name} className="preview-box-file-name">{file.name}</div>
				<ZeeroButton className="preview-box-delete" type={"none"} onClick={() => onDelete(file)}>
					<FontAwesomeIcon icon={faXmark}/>
				</ZeeroButton>
			</div>
	);
}
